import React, { useMemo, useState, useCallback } from 'react';
import { Avatar, Box, Button, Card, Chip, Collapse, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Iconify from '../../../components/Iconify';
import { capitalize, utcMoment } from '../../../utils/common';

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isUrl = (v) => typeof v === 'string' && /^https?:\/\/[^\s]+$/i.test(v);
const openInNew = (u) => window.open(u, '_blank', 'noopener,noreferrer');

const stringifyPrimitive = (v) => {
  if (v === undefined) return '∅';
  if (v === null) return 'null';
  if (typeof v === 'string') return v.trim() === '' ? '""' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
};

const initialsFrom = (name) => {
  const n = String(name || '').trim();
  if (!n) return '•';
  const parts = n.split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const toPathEntries = (obj, base = '') => {
  const out = [];
  if (obj === null || obj === undefined) return out;
  if (!isPlainObject(obj) && !Array.isArray(obj)) {
    out.push([base || '.', obj]);
    return out;
  }
  const stack = [[obj, base]];
  while (stack.length) {
    const [cur, prefix] = stack.pop();
    if (isPlainObject(cur)) {
      const keys = Object.keys(cur);
      if (keys.length === 0 && prefix) out.push([prefix, cur]);
      for (const k of keys) {
        const np = prefix ? `${prefix}.${k}` : k;
        const v = cur[k];
        if (isPlainObject(v)) {
          stack.push([v, np]);
        } else if (Array.isArray(v)) {
          out.push([np, v]);
        } else {
          out.push([np, v]);
        }
      }
    } else if (Array.isArray(cur)) {
      out.push([prefix || '.', cur]);
    } else {
      out.push([prefix || '.', cur]);
    }
  }
  return out;
};

const buildChangedToEntries = (before, after) => {
  const bMap = new Map(toPathEntries(before).map(([p, v]) => [p, v]));
  const aMap = new Map(toPathEntries(after).map(([p, v]) => [p, v]));
  const paths = new Set([...bMap.keys(), ...aMap.keys()]);
  const rows = [];
  for (const p of paths) {
    const from = bMap.has(p) ? bMap.get(p) : undefined;
    const to = aMap.has(p) ? aMap.get(p) : undefined;
    const same = (() => {
      if (from === to) return true;
      try { return JSON.stringify(from) === JSON.stringify(to); } catch { return false; }
    })();
    if (!same) rows.push({ path: p, to });
  }
  rows.sort((x, y) => x.path.localeCompare(y.path));
  return rows;
};

const normalizeToOnly = (rawJson) => {
  if (!rawJson || typeof rawJson !== 'object') return { mode: 'entries', entries: [] };
  if ('before' in rawJson && 'after' in rawJson) return { mode: 'entries', entries: buildChangedToEntries(rawJson.before || {}, rawJson.after || {}) };
  if ('old' in rawJson && 'new' in rawJson) return { mode: 'entries', entries: buildChangedToEntries(rawJson.old || {}, rawJson.new || {}) };
  if ('prev' in rawJson && 'next' in rawJson) return { mode: 'entries', entries: buildChangedToEntries(rawJson.prev || {}, rawJson.next || {}) };
  if ('previous' in rawJson && 'current' in rawJson) return { mode: 'entries', entries: buildChangedToEntries(rawJson.previous || {}, rawJson.current || {}) };
  const keys = Object.keys(rawJson);
  const looksLikePairs = keys.every((k) => rawJson[k] && typeof rawJson[k] === 'object' && ('to' in rawJson[k] || 'from' in rawJson[k]));
  if (looksLikePairs) {
    const entries = keys.map((k) => ({ path: k, to: rawJson[k]?.to ?? null }));
    return { mode: 'entries', entries };
  }
  return { mode: 'object', object: rawJson };
};

const PrettyField = ({ path }) => {
  const last = path.split('.').pop() || path;
  return <span>{capitalize(last)}</span>;
};

const UrlButtons = ({ urls }) => {
  const list = Array.isArray(urls) ? urls.filter((u) => isUrl(u)) : isUrl(urls) ? [urls] : [];
  if (!list.length) return null;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {list.map((u, idx) => (
        <Button key={`${u}-${idx}`} size="small" variant="outlined" endIcon={<Iconify icon="mdi:open-in-new" width={16} height={16} />} onClick={(e) => { e.stopPropagation(); openInNew(u); }}>
          Open
        </Button>
      ))}
    </Stack>
  );
};

const ArrayPrimitiveList = ({ items }) => {
  const urls = items.filter((v) => isUrl(v));
  const others = items.filter((v) => !isUrl(v));
  return (
    <Stack spacing={1}>
      <UrlButtons urls={urls} />
      {others.length ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {others.map((v, i) => (
            <Chip key={i} label={stringifyPrimitive(v)} size="small" />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
};

const ArrayObjectTable = ({ rows }) => {
  const theme = useTheme();
  const colsSet = new Set();
  rows.forEach((r) => Object.keys(r || {}).forEach((k) => colsSet.add(k)));
  const cols = Array.from(colsSet);
  if (!cols.length) return <Typography sx={{ fontSize: 13 }}>[{}]</Typography>;
  return (
    <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {cols.map((c) => (
              <TableCell key={c} sx={{ fontWeight: 600 }}>{capitalize(c)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, ridx) => (
            <TableRow key={ridx}>
              {cols.map((c) => {
                const val = r?.[c];
                const hasUrlKey = isPlainObject(val) && typeof val?.url === 'string' && isUrl(val.url);
                return (
                  <TableCell key={c} sx={{ fontSize: 13 }}>
                    {hasUrlKey ? <UrlButtons urls={val.url} /> : isPlainObject(val) || Array.isArray(val) ? <NestedValue value={val} /> : isUrl(val) ? <UrlButtons urls={val} /> : stringifyPrimitive(val)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const NestedValue = ({ value }) => {
  if (Array.isArray(value)) {
    const allPrimitives = value.every((v) => !isPlainObject(v) && !Array.isArray(v));
    if (allPrimitives) return <ArrayPrimitiveList items={value} />;
    const onlyObjects = value.every((v) => isPlainObject(v));
    if (onlyObjects) return <ArrayObjectTable rows={value} />;
    return (
      <Stack spacing={1}>
        <UrlButtons urls={value.filter((v) => isUrl(v))} />
        {value.map((v, i) => (
          <Box key={i}>
            {isPlainObject(v) || Array.isArray(v) ? <NestedValue value={v} /> : <Typography sx={{ fontSize: 13 }}>{stringifyPrimitive(v)}</Typography>}
          </Box>
        ))}
      </Stack>
    );
  }
  if (isPlainObject(value)) {
    const hasDirectUrl = typeof value?.url === 'string' && isUrl(value.url);
    const entries = Object.keys(value).map((k) => ({ k, v: value[k] }));
    return (
      <Stack spacing={1}>
        {hasDirectUrl ? <UrlButtons urls={value.url} /> : null}
        <Table size="small">
          <TableBody>
            {entries.map((r, idx) => (
              <TableRow key={idx}>
                <TableCell sx={{ width: '40%', fontWeight: 600 }}>{capitalize(r.k)}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {isPlainObject(r.v) || Array.isArray(r.v) ? <NestedValue value={r.v} /> : isUrl(r.v) ? <UrlButtons urls={r.v} /> : stringifyPrimitive(r.v)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    );
  }
  return isUrl(value) ? <UrlButtons urls={value} /> : <Typography sx={{ fontSize: 13 }}>{stringifyPrimitive(value)}</Typography>;
};

const ToOnlyTable = ({ entries }) => {
  const theme = useTheme();
  if (!entries.length) {
    return (
      <Card sx={{ p: 2, borderRadius: 2, border: `1px dashed ${theme.palette.divider}`, boxShadow: 'none', bgcolor: 'background.neutral' }}>
        <Typography variant="body2">No updates</Typography>
      </Card>
    );
  }
  return (
    <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: '40%' }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '60%' }}>To</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((r, i) => (
            <TableRow key={`${r.path}-${i}`}>
              <TableCell sx={{ fontSize: 13 }}><PrettyField path={r.path} /></TableCell>
              <TableCell sx={{ fontSize: 13 }}>
                {isPlainObject(r.to) || Array.isArray(r.to) ? <NestedValue value={r.to} /> : isUrl(r.to) ? <UrlButtons urls={r.to} /> : stringifyPrimitive(r.to)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const HistoryItem = ({ item, open, onToggle }) => {
  const theme = useTheme();
  const [innerOpen, setInnerOpen] = useState(false);
  const isControlled = typeof open === 'boolean';
  const isOpen = isControlled ? open : innerOpen;
  const handleToggle = useCallback(() => {
    if (typeof onToggle === 'function') onToggle();
    else setInnerOpen((p) => !p);
  }, [onToggle]);
  const parsed = useMemo(() => {
    const raw = typeof item?.json === 'object' ? item.json : (() => { try { return JSON.parse(item?.json || '{}'); } catch { return {}; } })();
    const shaped = normalizeToOnly(raw);
    return shaped;
  }, [item?.json]);
  return (
    <Card sx={{ p: 0, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}>
      <Stack direction="row" alignItems="center" spacing={1} onClick={handleToggle} sx={{ p: 2, cursor: 'pointer' }}>
        <Avatar sx={{ width: 28, height: 28 }}>{initialsFrom(item?.userName)}</Avatar>
        <Typography variant="subtitle2" sx={{ mr: 1 }}>{item?.userName || 'Unknown'}</Typography>
        <Typography variant="caption" color="text.secondary">{utcMoment(item?.dateTime).format('YYYY-MM-DD HH:mm:ss')}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={(e) => { e.stopPropagation(); handleToggle(); }} size="small" sx={{ ml: 1, bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}>
          <Iconify icon={isOpen ? 'solar:folder-open-outline' : 'solar:folder-outline'} width={18} height={18} />
        </IconButton>
      </Stack>
      <Collapse in={isOpen} unmountOnExit timeout="auto">
        <Box sx={{ px: 2, pb: 2 }}>
          {parsed.mode === 'entries' ? <ToOnlyTable entries={parsed.entries} /> : <NestedValue value={parsed.object} />}
        </Box>
      </Collapse>
    </Card>
  );
};

const HistoryToViewer = ({ items }) => {
  const theme = useTheme();
  const list = Array.isArray(items) ? items.slice().sort((a, b) => String(b?.dateTime || '').localeCompare(String(a?.dateTime || ''))) : [];
  const [openId, setOpenId] = useState(null);
  const makeKey = (h, idx) => h?.id ?? `${h?.dateTime || ''}-${idx}`;
  const onToggleItem = useCallback((key) => {
    setOpenId((prev) => (prev === key ? null : key));
  }, []);
  return (
    <Box sx={{ fontFamily: theme.typography.fontFamily, '& *': { fontFamily: 'inherit' } }}>
      <Stack spacing={2} maxWidth={1000} mx="auto">
        {list.length === 0 ? (
          <Card sx={{ p: 2, borderRadius: 2, border: `1px dashed ${theme.palette.divider}`, boxShadow: 'none', bgcolor: 'background.neutral' }}>
            <Typography variant="body2">No history yet</Typography>
          </Card>
        ) : null}
        {list.map((h, idx) => {
          const key = makeKey(h, idx);
          const open = openId === key;
          return <HistoryItem key={key} item={h} open={open} onToggle={() => onToggleItem(key)} />;
        })}
      </Stack>
    </Box>
  );
};

export default React.memo(HistoryToViewer);
